import { createSlice, PayloadAction, Draft } from "@reduxjs/toolkit";
import { WhatsappConversationSummaryDto } from "../../shared/dtos/whatsapp/WhatsappConversationSummaryDto";
import { WhatsappMessageDto } from "../../shared/dtos/whatsapp/WhatsappMessageDto";
import { PaginationDto } from "../../shared/dtos/common/PaginationDto";
import { ServerStatusPayload } from "../../shared/interfaces/ServerStatusPayload";

export interface WhatsappState {
  conversations: WhatsappConversationSummaryDto[];
  messages: WhatsappMessageDto[];
  activeConversationId: string | null;

  loadingConversations: boolean;
  loadingMessages: boolean;
  loadingInitiate: boolean;
  loadingReply: boolean;
  loadingResolve: boolean;

  errorConversations: string | null;
  errorMessages: string | null;
  errorInitiate: string | null;
  errorReply: string | null;
  errorResolve: string | null;

  conversationsPagination: Partial<PaginationDto>;
  messagesPagination: Partial<PaginationDto>;
  serverStatus: ServerStatusPayload | null;
}

const initialState: WhatsappState = {
  conversations: [],
  messages: [],
  activeConversationId: null,

  loadingConversations: false,
  loadingMessages: false,
  loadingInitiate: false,
  loadingReply: false,
  loadingResolve: false,

  errorConversations: null,
  errorMessages: null,
  errorInitiate: null,
  errorReply: null,
  errorResolve: null,

  conversationsPagination: {},
  messagesPagination: {},
  serverStatus: null,
};

export const whatsappSlice = createSlice({
  name: "whatsapp",
  initialState,
  reducers: {
    setLoadingConversations(state) {
      state.loadingConversations = true;
      state.errorConversations = null;
    },
    setLoadingMessages(state) {
      state.loadingMessages = true;
      state.errorMessages = null;
      state.messages = [];
    },
    setLoadingInitiate(state) {
      state.loadingInitiate = true;
      state.errorInitiate = null;
    },
    setLoadingReply(state) {
      state.loadingReply = true;
      state.errorReply = null;
    },
    setLoadingResolve(state) {
      state.loadingResolve = true;
      state.errorResolve = null;
    },

    setErrorConversations(state, action: PayloadAction<string | null>) {
      state.loadingConversations = false;
      state.errorConversations = action.payload;
    },
    setErrorMessages(state, action: PayloadAction<string | null>) {
      state.loadingMessages = false;
      state.errorMessages = action.payload;
    },
    setErrorInitiate(state, action: PayloadAction<string | null>) {
      state.loadingInitiate = false;
      state.errorInitiate = action.payload;
    },
    setErrorReply(state, action: PayloadAction<string | null>) {
      state.loadingReply = false;
      state.errorReply = action.payload;
    },
    setErrorResolve(state, action: PayloadAction<string | null>) {
      state.loadingResolve = false;
      state.errorResolve = action.payload;
    },

    setConversations(
      state,
      action: PayloadAction<WhatsappConversationSummaryDto[]>,
    ) {
      state.loadingConversations = false;
      state.conversations = action.payload as Draft<
        WhatsappConversationSummaryDto[]
      >;
    },
    setMessages(state, action: PayloadAction<WhatsappMessageDto[]>) {
      state.loadingMessages = false;
      // Reverse to show oldest first for chat flow
      state.messages = action.payload.reverse() as Draft<WhatsappMessageDto[]>;
    },
    setActiveConversationId(state, action: PayloadAction<string | null>) {
      state.activeConversationId = action.payload;
    },
    setConversationsPagination(
      state,
      action: PayloadAction<Partial<PaginationDto>>,
    ) {
      state.conversationsPagination = action.payload;
    },
    setMessagesPagination(
      state,
      action: PayloadAction<Partial<PaginationDto>>,
    ) {
      state.messagesPagination = action.payload;
    },

    initiateSuccess(state) {
      state.loadingInitiate = false;
    },
    replySuccess(state, action: PayloadAction<WhatsappMessageDto>) {
      state.loadingReply = false;
      state.messages.push(action.payload as Draft<WhatsappMessageDto>);
      state.conversations = state.conversations.map((c) =>
        c.id === state.activeConversationId
          ? {
              ...c,
              agentHasResponded: true,
              lastActivityAt: action.payload.createdAt,
            }
          : c,
      );
    },
    resolveSuccess(state, action: PayloadAction<string>) {
      state.loadingResolve = false;
      state.conversations = state.conversations.filter(
        (c) => c.id !== action.payload,
      );
      if (state.activeConversationId === action.payload) {
        state.activeConversationId = null;
        state.messages = [];
      }
    },
    setServerStatus(state, action: PayloadAction<ServerStatusPayload | null>) {
      state.serverStatus = action.payload;
    },
  },
});

export const whatsappActions = whatsappSlice.actions;
export default whatsappSlice.reducer;
